/**
 * Copyright 2020 Sebastian Roming
 *
 * @author Sebastian Roming <sebastian@webmonkey.io>
 * @since 1.0.0
 **/

import {
  css,
  CSSResultArray,
  customElement,
  html,
  internalProperty,
  LitElement,
  property,
  PropertyValues,
  query,
  TemplateResult
}                                                    from 'lit-element';
import { render }                                    from 'lit-html';

/**
 * vaadin imports
 */
import '@vaadin/vaadin-lumo-styles/all-imports';
import '@vaadin/vaadin-app-layout/vaadin-app-layout';
import '@vaadin/vaadin-app-layout/vaadin-drawer-toggle';
import '@vaadin/vaadin-tabs/vaadin-tabs';
import '@vaadin/vaadin-tabs/vaadin-tab';
import '@vaadin/vaadin-button/vaadin-button';
import '@vaadin/vaadin-login/vaadin-login-overlay';
import '@vaadin/vaadin-dialog/vaadin-dialog';
import '@vaadin/vaadin-split-layout/vaadin-split-layout';
import '@vaadin/vaadin-grid/vaadin-grid';
import '@vaadin/vaadin-grid/vaadin-grid-filter-column';
import '@vaadin/vaadin-checkbox/vaadin-checkbox';
import type { CheckboxElement }                      from '@vaadin/vaadin-checkbox';
import type { GridElement, GridItem, GridItemModel } from '@vaadin/vaadin-grid';
import type { GridColumnElement }                    from '@vaadin/vaadin-grid/vaadin-grid-column.js';

/**
 * design/styling imports
 */
import '@pwabuilder/pwaauth';
import '@material/mwc-switch';
import '@material/mwc-button';
import '../styles/register-styles';
import { CSSModule }                                 from '../styles/css-utils';
import { ResponsiveStyles }                          from '../styles/responsive-styles';

/**
 * functional imports
 */
import ls, { get, remove }                           from 'local-storage';
import './loading-elem';

/**
 * FIREBASE imports
 */
import firebase                                      from 'firebase/app';
import 'firebase/auth';
import 'firebase/messaging';


enum FETCH_TYPE {
  FMS    = 'fms',
  ZVEI   = 'zvei',
  POCSAG = 'pocsag',
  META   = 'meta',
  AUTH   = 'auth',
  CHECK  = 'check'
}

interface IFms {
  fmsTlgId: number,
  dateTime: string,
  identifier: string,
  status: number,
  description: string
}

interface Alarm {
  addressId: number,
  address: string,
  dateTime: string,
  description: string
}

interface AlarmZvei extends Alarm {
  zveiId: number,
  identifier: string
}

interface AlarmPocsag extends Alarm {
  pocsagAlarmId: number,
  subaddress: string,
  alarmtext: string
}

interface IMetadata {
  lastFetched?: number,
  lastIdPocsag?: number,
  lastIdZvei?: number,
  lastIdFms?: number
}

enum LOCALSTORAGE_KEYS {
  ACCESS_TOKEN = 'alarm.dunningen112.de.security.accesstoken',
  AUTO_REFRESH = 'alarm.dunningen112.de.settings.auto.refresh'
}

// const SERVER_URL = 'http://localhost:9000';
const SERVER_URL = 'https://server.alarm.dunningen112.de';
// const SERVER_URL = process.env.APPLICATION_URL_SERVER;

const itemCache = new WeakMap<HTMLElement>();

@customElement('dfms-application')
export class DfmsApplication extends LitElement {

  @property({ type: Array, hasChanged: () => true }) _fmsData: Array<IFms>           = [];
  @property({ type: Array, hasChanged: () => true }) _zveiData: Array<AlarmZvei>     = [];
  @property({ type: Array, hasChanged: () => true }) _pocsagData: Array<AlarmPocsag> = [];
  @property({ type: Boolean, hasChanged: () => true }) loggedIn: boolean             = false;
  @property({ type: Boolean }) hideForgotPassword: boolean | null                    = true;
  @property({ type: Boolean, hasChanged: () => true }) _autoUpdate: boolean          = false;
  @property({ type: Object }) _fetchMetaData: IMetadata                              = {};
  @property({ type: Function }) __asyncRefreshHandler: any | null                    = null;

  @internalProperty() __isMobile: boolean = /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|ipad|iris|kindle|Android|Silk|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(navigator.userAgent) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(navigator.userAgent.substr(0, 4));

  @query('vaadin-grid#pocsagGrid')
  private grid!: GridElement;

  private _boundDateTimeColumnRenderer                  = this._dateTimeRowRenderer.bind(this);
  private _boundDateTimeDescriptionMobileColumnRenderer = this._dateTimeDescriptionRowMobileRenderer.bind(this);
  private _boundStatusColumnRenderer                    = this._statusRowRenderer.bind(this);
  private _boundKennungColumnRenderer                   = this._kennungRowRenderer.bind(this);
  private _boundRicAddressColumnRenderer                = this._ricAddressRowRenderer.bind(this);
  private _boundToggleDetailsRenderer                   = this._toggleDetailsRenderer.bind(this);
  private _boundPocsagRowDetailsRenderer                = this._pocsagAlarmTextRowRender.bind(this);
  private _boundFmsDateAndKennungMobileRenderer         = this._fmsDateAndKennungMobileRenderer.bind(this);

  // --------------------------------------------------------------------------------------
  static get styles(): CSSResultArray {
    return [
      CSSModule('lumo-typography'),
      CSSModule('lumo-color'),
      css`
        vaadin-grid {
          height: 85vh;
        }
        
        vaadin-grid.half {
          height: 42vh;
        }
        
        vaadin-grid.third {
          height: 28vh;
        }
        
        vaadin-grid.two-third {
          height: 56vh;
        }
        
        span.pocsagDescription,
        span.fmsDescription {  
         font-weight: bold;
        }
        
        vaadin-split-layout[orientation="vertical"] vaadin-grid#pocsagGrid {
          height: 70vh;
        }
        
        vaadin-split-layout[orientation="vertical"] vaadin-grid#pocsagGrid vaadin-grid-filter-column[part="pocsag-grid-column-address"] {
          display: none;
        }
        
        .application-title {
          font-weight: bold;
          font-size: 24px;
          color: #95020a;
        }
        
        .auto-refresh {
          display: inline;
          margin-right: 25px;
          
          --mdc-theme-secondary: #95020a;
          --mdc-theme-on-surface: #adadad;
          --mdc-theme-surface: white;
        }
        
        .auto-refresh mwc-switch {
          margin-left:5px;
        }
        
        mwc-button {
          display:inline;
          margin-left:5px;
          margin-top:5px;
          --mdc-theme-primary: #95020a;
          --mdc-theme-secondary: #95020a;
          --mdc-theme-on-surface: #adadad;
          --mdc-theme-surface: white;
        }
        
        pwa-auth {
        }
        
        loading-elem {
          opacity: 1;
          transition: opacity 1s; 
        }
        
        loading-elem.ld-fadeout {
          opacity: 0;
        }
    `,
      ResponsiveStyles];
  }

  // --------------------------------------------------------------------------------------
  endpoint(type: FETCH_TYPE, lastFetchId: number | null = null): string {
    if (type === FETCH_TYPE.AUTH) {
      return `${SERVER_URL}/auth`;
    }

    if (type === FETCH_TYPE.CHECK) {
      return `${SERVER_URL}/check`;
    }

    if (lastFetchId != null) {
      return `${SERVER_URL}/${type}/${lastFetchId}/`;
    }

    return `${SERVER_URL}/${type}/latest`;
  }

  // --------------------------------------------------------------------------------------
  firstUpdated(): void {
    if (this.loggedIn) {
      this.fetchData();
      this._checkAutoRefresh();
    } else {
      const accessToken = ls(LOCALSTORAGE_KEYS.ACCESS_TOKEN);
      if (accessToken != null) {
        this._checkToken();
      } else {
        this._hideLoadingElem();
      }
    }

  }

  // --------------------------------------------------------------------------------------
  _hideLoadingElem(): void {
    const _loadingElem = this.shadowRoot!.querySelector('loading-elem');
    if (_loadingElem !== null) {
      _loadingElem.remove();
    }
  }

  // --------------------------------------------------------------------------------------
  fetchData(withLastFetchedTimestamp: boolean = false): void {

    this._fetchMeta();
    this._fetchFms(withLastFetchedTimestamp);
    /* this._fetchZvei(withLastFetchedTimestamp); */
    this._fetchPocsag(withLastFetchedTimestamp);

  }

  // --------------------------------------------------------------------------------------
  _fetchFms(withLastFetchedTimestamp: boolean): void {
    const _endpoint = withLastFetchedTimestamp ? this.endpoint(FETCH_TYPE.FMS, this._fetchMetaData.lastIdFms) : this.endpoint(FETCH_TYPE.FMS);

    const headers = new Headers();
    headers.set('Authorization', `Bearer ${ls(LOCALSTORAGE_KEYS.ACCESS_TOKEN)}`);
    fetch(_endpoint, { headers })
    .then((r: Response) => r.json())
    .then(data => {
      if (withLastFetchedTimestamp) {
        this._fmsData = [...data, ...this._fmsData];
      } else {
        this._fmsData = data;
      }
      if (data.length > 0) {
        this._fetchMetaData.lastIdFms = data[0].fmsId;
      }
    });
  }

  // --------------------------------------------------------------------------------------
  _fetchZvei(withLastFetchedTimestamp: boolean): void {
    const _endpoint = withLastFetchedTimestamp ? this.endpoint(FETCH_TYPE.ZVEI, this._fetchMetaData.lastIdZvei) : this.endpoint(FETCH_TYPE.ZVEI);

    const headers = new Headers();
    headers.set('Authorization', `Bearer ${ls(LOCALSTORAGE_KEYS.ACCESS_TOKEN)}`);
    fetch(_endpoint, { headers })
    .then((r: Response) => r.json())
    .then(data => {
      if (withLastFetchedTimestamp) {
        this._zveiData = [...data, ...this._zveiData];
      } else {
        this._zveiData = data;
      }
      if (data.length > 0) {
        this._fetchMetaData.lastIdZvei = data[0].zveiId;
      }
    });
  }

  // --------------------------------------------------------------------------------------
  _fetchPocsag(withLastFetchedTimestamp: boolean): void {
    const _endpoint = withLastFetchedTimestamp ? this.endpoint(FETCH_TYPE.POCSAG, this._fetchMetaData.lastIdPocsag) : this.endpoint(FETCH_TYPE.POCSAG);

    const headers = new Headers();
    headers.set('Authorization', `Bearer ${ls(LOCALSTORAGE_KEYS.ACCESS_TOKEN)}`);
    fetch(_endpoint, { headers })
    .then((r: Response) => r.json())
    .then(data => {
      if (withLastFetchedTimestamp) {
        this._pocsagData = [...data, ...this._pocsagData];
      } else {
        this._pocsagData = data;
      }
      if (data.length > 0) {
        this._fetchMetaData.lastIdPocsag = data[0].pocsagAlarmId;
      }
    });
  }

  // --------------------------------------------------------------------------------------
  _fetchMeta(): void {
    const headers = new Headers();
    headers.set('Authorization', `Bearer ${ls(LOCALSTORAGE_KEYS.ACCESS_TOKEN)}`);
    fetch(this.endpoint(FETCH_TYPE.META), { headers })
    .then((r: Response) => r.json())
    .then(data => {
      this._fetchMetaData.lastFetched = data.lastFetched;
    });
  }

  // --------------------------------------------------------------------------------------
  _checkToken(): void {
    const headers = new Headers();
    headers.set('Authorization', `Bearer ${ls(LOCALSTORAGE_KEYS.ACCESS_TOKEN)}`);
    fetch(this.endpoint(FETCH_TYPE.CHECK), { headers })
    .then((r: Response) => r.json())
    .then(data => {
      this._hideLoadingElem();
      if (data.message != null) {
        remove(LOCALSTORAGE_KEYS.ACCESS_TOKEN);
      } else {
        this.loggedIn = true;
      }
    });
  }

  // --------------------------------------------------------------------------------------
  render(): TemplateResult {

    const _pocsagGrid = [];
    const _fmsGrid    = [];

    if (!this.__isMobile) {
      _pocsagGrid.push(html`<vaadin-grid-column width="25%" path="dateTime" header="Datum / Uhrzeit" .renderer='${this._boundDateTimeColumnRenderer}'></vaadin-grid-column>`);
      _pocsagGrid.push(html`<vaadin-grid-filter-column width="15%" path="address" header="Adresse" .renderer='${this._boundRicAddressColumnRenderer}'></vaadin-grid-filter-column>`);
      _pocsagGrid.push(html`<vaadin-grid-filter-column width="40%" path="description" header="Text"></vaadin-grid-filter-column>`);
      _pocsagGrid.push(html`<!--<vaadin-grid-column width="20%" .renderer="${this._boundToggleDetailsRenderer}"></vaadin-grid-column>-->`);

      _fmsGrid.push(html`<vaadin-grid-column width="25%" path="dateTime" header="Datum / Uhrzeit" .renderer='${this._boundDateTimeColumnRenderer}'></vaadin-grid-column>`);
      _fmsGrid.push(html`<vaadin-grid-column width="10%" path="status" header="Status" .renderer='${this._boundStatusColumnRenderer}'></vaadin-grid-column>`);
      _fmsGrid.push(html`<vaadin-grid-filter-column width="65%" path="description" header="Fahrzeugkennung" .renderer='${this._boundKennungColumnRenderer}'></vaadin-grid-filter-column>`);
    } else {
      _pocsagGrid.push(html`<vaadin-grid-filter-column width="30%" path="address" header="Adresse" .renderer='${this._boundRicAddressColumnRenderer}'></vaadin-grid-filter-column>`);
      _pocsagGrid.push(html`<vaadin-grid-filter-column width="70%" path="dateTime" header="Datum / Uhrzeit / Text" .renderer='${this._boundDateTimeDescriptionMobileColumnRenderer}'></vaadin-grid-filter-column>`);

      _fmsGrid.push(html`<vaadin-grid-column width="20%" path="status" header="Status" .renderer='${this._boundStatusColumnRenderer}'></vaadin-grid-column>`);
      _fmsGrid.push(html`<vaadin-grid-filter-column width="80%" path="description" header="Fahrzeugkennung" .renderer='${this._boundFmsDateAndKennungMobileRenderer}'></vaadin-grid-filter-column>`);
    }

    return html`
      <loading-elem></loading-elem>

      <vaadin-login-overlay 
        title="DunningenFMS"
        description="Anmelden, um Daten abrufen zu können."
        no-forgot-password
        ?opened="${!this.loggedIn}"
        @login="${(evt: CustomEvent) => this._handleLogin(evt)}"></vaadin-login-overlay>
      
      <vaadin-dialog id="feedbackDialog">
        <template>Anmeldung l&auml;uft...</template>
      </vaadin-dialog>
      <vaadin-dialog id="supportDialog">
        <template>Please contact support.</template>
      </vaadin-dialog>

      <vaadin-app-layout>
        <div slot="navbar" class="application-title">DunningenFMS</div>
        <div slot="navbar" style="margin-left: auto; padding-right: 15px;">
          <div>
            <div class="auto-refresh">Autom. aktualisieren&nbsp;<mwc-switch id="auto-update-switch" @change="${() => this._changeAutoUpdate()}"></mwc-switch></div>
            <pwa-auth disabled signInButtonText="Anmelden" @signin-completed="${(evt: CustomEvent) => this._handlePwaAuth(evt)}" menuPlacement="end" appearance="button" googlekey="940106373167-rhe5a4fubo03vrgk1p6fkole4ccoc96g.apps.googleusercontent.com"></pwa-auth>
            <!--<mwc-button outlined label="Logout" @click="${() => this._handleLogout()}"></mwc-button>-->   
            <!--<mwc-button outlined label="Google Login" @click="${() => this._handleGoogleLogin()}"></mwc-button>-->
          </div>
        </div>
        
      
        <div class="content">
                  
          <vaadin-split-layout orientation="${this.__isMobile ? 'vertical' : 'horizontal'}">
          
            <vaadin-grid id="pocsagGrid" .items="${this._pocsagData}" .rowDetailsRenderer="${this._boundPocsagRowDetailsRenderer}">
              ${_pocsagGrid}
            </vaadin-grid>
            
            <vaadin-grid id="fms" .items="${this._fmsData}" .cellClassNameGenerator="${this.cellClassGenerator}">
              <!--<vaadin-grid-column width="25%" path="dateTime" header="Datum / Uhrzeit" .renderer='${this._boundDateTimeColumnRenderer}'></vaadin-grid-column>
              <vaadin-grid-column width="10%" path="status" header="Status" .renderer='${this._boundStatusColumnRenderer}'></vaadin-grid-column>
              <vaadin-grid-filter-column width="65%" path="description" header="Fahrzeugkennung" .renderer='${this._boundKennungColumnRenderer}'></vaadin-grid-filter-column>-->
              ${_fmsGrid}
            </vaadin-grid>
          
          </vaadin-split-layout>

        </div>
      </vaadin-app-layout>
    `;
  }

  // --------------------------------------------------------------------------------------
  cellClassGenerator(column: GridColumnElement, model: GridItemModel): string {
    const resultObject = model.item as { status: number };

    let classes = ' ';
    if (column.path === 'status') {
      classes += ' status';

      switch (resultObject.status) {
        case 1:
        case 2:
          classes += ' green';
          break;
        case 3:
        case 4:
          classes += ' red';
          break;
        case 6:
          classes += ' grey';
          break;
      }

    }

    return classes;
  }

  // --------------------------------------------------------------------------------------
  _dateFormat(dateTimeStr: string): string {
    const dateObject = new Date(dateTimeStr);
    const options    = {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    };

    return new Intl.DateTimeFormat('de-de', options).format(dateObject);
  }

  // --------------------------------------------------------------------------------------
  protected _checkAutoRefresh(): void {
    const _loaded = get(LOCALSTORAGE_KEYS.AUTO_REFRESH);
    if (_loaded) {
      this.__enableAutoUpdate();
    }
  }

  // --------------------------------------------------------------------------------------
  protected updated(_changedProperties: PropertyValues): void {
    super.updated(_changedProperties);

    if (_changedProperties.has('loggedIn')) {
      if (this.loggedIn) {
        this.fetchData();
        this._checkAutoRefresh();
      }
    }
  }

  // --------------------------------------------------------------------------------------
  protected _dateTimeRowRenderer(root: HTMLElement, _grid: GridElement, model: GridItemModel): void {
    const resultObject = model.item as { dateTime: string };
    render(html`${this._dateFormat(resultObject.dateTime)}`, root);
  }

  // --------------------------------------------------------------------------------------
  protected _dateTimeDescriptionRowMobileRenderer(root: HTMLElement, _grid: GridElement, model: GridItemModel): void {
    const resultObject = model.item as { dateTime: string, description: string };
    render(html`<span class="pocsagDescription">${resultObject.description}</span><br />${this._dateFormat(resultObject.dateTime)}`, root);
  }

  // --------------------------------------------------------------------------------------
  protected _statusRowRenderer(root: HTMLElement, _grid: GridElement, model: GridItemModel): void {
    const resultObject = model.item as { status: string };
    render(html`${resultObject.status}`, root);
  }

  // --------------------------------------------------------------------------------------
  protected _kennungRowRenderer(root: HTMLElement, _grid: GridElement, model: GridItemModel): void {
    const resultObject = model.item as { description: string, identifier: string };
    render(html`${(resultObject.description != null) ? resultObject.description : resultObject.identifier}`, root);
  }

  // --------------------------------------------------------------------------------------
  protected _fmsDateAndKennungMobileRenderer(root: HTMLElement, _grid: GridElement, model: GridItemModel): void {
    const resultObject = model.item as { dateTime: string, description: string, identifier: string };
    render(html`<span class="fmsDescription">${(resultObject.description != null) ? resultObject.description : resultObject.identifier}</span><br />${this._dateFormat(resultObject.dateTime)}`, root);
  }

  // --------------------------------------------------------------------------------------
  protected _onDetailsCheckboxChange(e: CustomEvent): void {
    const checkbox = e.target as HTMLElement;
    this._toggleDetails(e.detail.value, itemCache.get(checkbox.parentNode as HTMLElement));
  }

  // --------------------------------------------------------------------------------------
  protected _toggleDetails(value: boolean, item: GridItem): void {
    if (value) {
      this.grid.openItemDetails(item);
    } else {
      this.grid.closeItemDetails(item);
    }
  }

  // --------------------------------------------------------------------------------------
  protected _toggleDetailsRenderer(root: HTMLElement, _column: GridColumnElement, model: GridItemModel): void {
    if (!root.firstElementChild) {
      render(html`<vaadin-checkbox @checked-changed="${this._onDetailsCheckboxChange}">Alarmtext</vaadin-checkbox>`, root, { eventContext: this });
    }
    const { item } = model;
    itemCache.set(root, item);
    const detailsOpened                                 = this.grid.detailsOpenedItems || [];
    (root.firstElementChild as CheckboxElement).checked = detailsOpened.indexOf(item) > -1;
  }

  // --------------------------------------------------------------------------------------
  protected _pocsagAlarmTextRowRender(root: HTMLElement, _grid: GridElement, model: GridItemModel): void {
    const alarm = model.item as { alarmtext: string };
    render(html`${alarm.alarmtext}`, root);
  }

  // --------------------------------------------------------------------------------------
  protected _ricAddressRowRenderer(root: HTMLElement, _grid: GridElement, model: GridItemModel): void {
    const resultObject = model.item as { address: string, subaddress: string };
    render(html`${resultObject.address} ${resultObject.subaddress}`, root);
  }

  // --------------------------------------------------------------------------------------
  protected _handleLogin(evt: CustomEvent): void {
    const vaadinLoginOverlay = this.shadowRoot!.querySelector('vaadin-login-overlay');
    const detail             = evt.detail,
          username           = detail.username,
          password           = detail.password;

    const that = this;

    const data = {
      username: btoa(username),
      password: btoa(password)
    };

    const headers = new Headers();
    headers.set('Content-Type', 'application/json');

    fetch(this.endpoint(FETCH_TYPE.AUTH), { method: 'POST', cache: 'no-cache', body: JSON.stringify(data) })
    .then((r: Response) => r.json())
    .then(data => {
      if (data.signIn === true) {
        // @ts-ignore
        vaadinLoginOverlay.opened = false;
        that.loggedIn             = true;
        ls(LOCALSTORAGE_KEYS.ACCESS_TOKEN, data.access_token);
      } else {
        // @ts-ignore
        vaadinLoginOverlay.error = true;
      }
    })
    .catch(e => console.log('e', e));

  }

  // --------------------------------------------------------------------------------------
  protected _handleLogout(): void {
    this.loggedIn = false;
    remove(LOCALSTORAGE_KEYS.ACCESS_TOKEN);
  }

  // --------------------------------------------------------------------------------------
  protected _handlePwaAuth(evt: CustomEvent): void {
    const signIn = evt.detail;
    if (signIn.error) {
      console.error('Sign in failed', signIn.error);
    } else {
      console.log('Email: ', signIn.email);
      console.log('Name: ', signIn.name);
      console.log('Picture: ', signIn.imageUrl);
      console.log('Access token', signIn.accessToken);
      console.log('Access token expiration date', signIn.accessTokenExpiration);
      console.log('Provider (MS, Google, FB): ', signIn.provider);
      console.log('Raw data from provider: ', signIn.providerData);
    }
  }

  // --------------------------------------------------------------------------------------
  protected _handleGoogleLogin(): void {
    const provider = new firebase.auth.GoogleAuthProvider();
    firebase.auth().signInWithPopup(provider).then((result: firebase.auth.UserCredential) => {
      console.log('Sign in successful', result);

      /*firebase.messaging().getToken().then((currentToken) => {
        if (currentToken) {
          console.log('Current Token', currentToken);
        } else {
          // Show permission request.
          console.log('No Instance ID token available. Request permission to generate one.');
        }
      }).catch((err) => {
        console.log('An error occurred while retrieving token. ', err);
      });*/

      //const headers = new Headers();
      //headers.set('X-Firebase-Access', _signedInAccessToken);
      //fetch('http://localhost:9000/firebaseTopics', {headers})
      //.then((r: Response) => console.log('r', r));

    }).catch(error => {
      console.error('Sign in failed', error);
    });
  }

  // --------------------------------------------------------------------------------------
  protected _changeAutoUpdate(): void {
    const _elem: HTMLInputElement | null = this.shadowRoot!.querySelector('mwc-switch#auto-update-switch');

    if (_elem!.checked && this.__asyncRefreshHandler === null) {
      this.__enableAutoUpdate();
    } else {
      this.__disableAutoUpdate();
    }
  }

  // --------------------------------------------------------------------------------------
  private __enableAutoUpdate(): void {
    ls(LOCALSTORAGE_KEYS.AUTO_REFRESH, true);
    const _elem: HTMLInputElement | null = this.shadowRoot!.querySelector('mwc-switch#auto-update-switch');
    _elem!.checked                       = true;
    this.__asyncRefreshHandler           = setInterval(() => {
      this.fetchData(true);
    }, 10000);
  }

  // --------------------------------------------------------------------------------------
  private __disableAutoUpdate(): void {
    ls(LOCALSTORAGE_KEYS.AUTO_REFRESH, false);
    if (this.__asyncRefreshHandler != null) {
      clearInterval(this.__asyncRefreshHandler);
    }
    this.__asyncRefreshHandler = null;
  }

}
